import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { ArrowClockwise, Compass, Info, CheckCircle } from "@phosphor-icons/react";
import { Empty, ResetConfirm } from "./IQTestCoachPanel";

/**
 * Job-coach view of a coachee's Werkwaarden-test (Schwartz).
 * States: not_started → selection → comparing (X/Y pairs) → completed (ranked list).
 */
export default function WorkValuesCoachPanel({ coacheeId, coacheeName }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const fetchSession = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/work-values`);
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchSession(); }, [coacheeId]);

  const handleReset = async () => {
    setResetting(true);
    setError("");
    try {
      await api.post(`/coachees/${coacheeId}/work-values/reset`);
      setConfirmOpen(false);
      await fetchSession();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setResetting(false); }
  };

  const state = session?.state || "not_started";
  const pairsKnown = session?.pairs_known || 0;
  const pairsTotal = session?.pairs_total || 0;
  const selected = session?.selected_values || [];
  const result = session?.result || [];

  return (
    <div className="space-y-4 max-w-3xl" data-testid="work-values-coach-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          <Compass size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">Werkwaarden-test (Schwartz)</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              {coacheeName || "De coachee"} kiest eerst 10 waarden en rangschikt deze via paarsgewijze vergelijkingen.
            </p>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-zinc-500" data-testid="work-values-coach-counter">
                Status: <strong className="text-zinc-900">{wvStateLabel(state)}</strong>
                {state === "comparing" && <span> — {pairsKnown} / {pairsTotal} vergelijkingen klaar</span>}
                {state === "selection" && <span> — {selected.length} / 10 waarden gekozen</span>}
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={resetting || state === "not_started"}
                data-testid="work-values-coach-reset-btn"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
              >
                <ArrowClockwise size={16} /> Test resetten
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="work-values-coach-error">{error}</div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : state === "not_started" ? (
        <Empty coacheeName={coacheeName} testid="work-values-coach-empty" text="heeft de werkwaarden-test nog niet gestart" />
      ) : state !== "completed" ? (
        <div className="neuro-card p-6 bg-white" data-testid="work-values-coach-in-progress">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">Test in uitvoering</h4>
              <p className="text-sm text-zinc-600 mt-1">
                {state === "selection"
                  ? `${selected.length} van de 10 waarden gekozen. Daarna volgen ~${pairsTotal} paarsgewijze vergelijkingen.`
                  : `${pairsKnown} van de ${pairsTotal} paarsgewijze vergelijkingen afgerond.`}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="neuro-card p-6 bg-white" data-testid="work-values-coach-result">
          <div className="flex items-start gap-3">
            <CheckCircle size={22} className="text-[#43A047] shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-display text-base font-bold tracking-tight">Rangschikking werkwaarden</h4>
              <ol className="mt-3 space-y-1.5 list-decimal list-inside" data-testid="work-values-coach-ranking">
                {result.map((value, idx) => (
                  <li key={value} className="text-sm">
                    <span className={"font-medium " + (idx < 3 ? "text-[#1E88E5]" : "text-zinc-800")}>
                      {value}
                    </span>
                  </li>
                ))}
              </ol>
              {session?.completed_at && (
                <div className="mt-3 text-xs text-zinc-400">
                  Afgerond op {new Date(session.completed_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <ResetConfirm
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleReset}
          busy={resetting}
          coacheeName={coacheeName}
          testName="werkwaarden-test"
          testid="work-values-coach"
        />
      )}
    </div>
  );
}

function wvStateLabel(state) {
  if (state === "completed") return "Afgerond";
  if (state === "comparing") return "Vergelijken";
  if (state === "selection") return "Waarden kiezen";
  return "Nog niet gestart";
}
